import { ArrowRight } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEventHandler,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatPostalAddress } from '@/lib/format'
import { cn } from '@/lib/utils'
import { formatTimeslot, sortJobsByStartTime } from './grid-helpers'
import {
  PRELOAD_POPOVER_DELAY_MS,
  jobPreloadTargetProps,
  parsePreloadJobIds,
} from './job-detail-preload'
import { useJobDetailPreloadQueue } from './useAgendaJobPreload'
import type { Job } from '@/types'

export type PopoverPointerSide = 'left' | 'right'

const POPOVER_MAX_WIDTH = 620
const POPOVER_GAP = 8
const VIEWPORT_PADDING = 12

interface PopoverPosition {
  top: number
  left: number
  width: number
  pointerSide: PopoverPointerSide
  pointerTop: number
  anchorElement?: HTMLElement
}

function computePopoverPosition(
  anchorRect: DOMRect,
  popoverHeight: number
): PopoverPosition {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const width = Math.min(
    POPOVER_MAX_WIDTH,
    Math.max(0, viewportWidth - VIEWPORT_PADDING * 2)
  )
  const spaceRight = viewportWidth - anchorRect.right - VIEWPORT_PADDING
  const spaceLeft = anchorRect.left - VIEWPORT_PADDING
  const showOnRight =
    spaceRight >= width + POPOVER_GAP || spaceRight >= spaceLeft
  const pointerSide: PopoverPointerSide = showOnRight ? 'left' : 'right'

  const preferredLeft = showOnRight
    ? anchorRect.right + POPOVER_GAP
    : anchorRect.left - width - POPOVER_GAP
  const left = Math.max(
    VIEWPORT_PADDING,
    Math.min(preferredLeft, viewportWidth - width - VIEWPORT_PADDING)
  )

  const anchorCenterY = anchorRect.top + anchorRect.height / 2
  const maxTop = Math.max(
    VIEWPORT_PADDING,
    viewportHeight - popoverHeight - VIEWPORT_PADDING
  )
  const top = Math.max(
    VIEWPORT_PADDING,
    Math.min(anchorCenterY - popoverHeight / 2, maxTop)
  )
  const pointerTop = Math.max(
    12,
    Math.min(
      anchorCenterY - top,
      Math.max(12, Math.min(popoverHeight - 12, viewportHeight - top - 12))
    )
  )

  return { top, left, width, pointerSide, pointerTop }
}

export interface MonthDayJobsPopoverProps {
  jobs: Job[]
  onViewJob: (job: Job) => void
  open: boolean
  className?: string
  pointerSide?: PopoverPointerSide
  anchorElement?: HTMLElement | null
  onMouseEnter?: MouseEventHandler<HTMLDivElement>
  onMouseLeave?: MouseEventHandler<HTMLDivElement>
  onClick?: MouseEventHandler<HTMLDivElement>
}

// Hover popup for a month-view cell (AC-2.3.1): lists jobs for one
// technician-day with client name, address, timeslot, and View Details.
export function MonthDayJobsPopover({
  jobs,
  onViewJob,
  open,
  className,
  pointerSide = 'left',
  anchorElement,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: MonthDayJobsPopoverProps) {
  const { t } = useTranslation('jobs')
  const sorted = useMemo(() => sortJobsByStartTime(jobs), [jobs])
  const preloadQueue = useJobDetailPreloadQueue()
  const sortedJobIdsKey = sorted.map(job => job.id).join(',')

  // An open popover moves its jobs to the front of the preload queue, after a
  // short delay so cells merely swept over by the mouse trigger nothing.
  useEffect(() => {
    if (!preloadQueue || !open || !sortedJobIdsKey) return
    const timer = window.setTimeout(
      () =>
        preloadQueue.setSource('popover', parsePreloadJobIds(sortedJobIdsKey)),
      PRELOAD_POPOVER_DELAY_MS
    )
    return () => {
      window.clearTimeout(timer)
      preloadQueue.setSource('popover', [])
    }
  }, [preloadQueue, open, sortedJobIdsKey])
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<PopoverPosition | null>(null)

  const updatePosition = useCallback(() => {
    if (!anchorElement) return
    const estimatedHeight = Math.min(40 + sorted.length * 64, 320) + 2
    const popoverHeight =
      popoverRef.current?.getBoundingClientRect().height ?? estimatedHeight
    setPosition({
      ...computePopoverPosition(
        anchorElement.getBoundingClientRect(),
        popoverHeight
      ),
      anchorElement,
    })
  }, [anchorElement, sorted.length])

  useLayoutEffect(() => {
    if (!open || !anchorElement) return

    updatePosition()
    const frame = window.requestAnimationFrame(updatePosition)
    return () => window.cancelAnimationFrame(frame)
  }, [open, anchorElement, updatePosition])

  useEffect(() => {
    if (!open || !anchorElement) return

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, anchorElement, updatePosition])

  if (!open || sorted.length === 0) return null

  const resolvedPointerSide = position?.pointerSide ?? pointerSide
  const content = (
    <div
      ref={popoverRef}
      className={cn('relative z-[200] w-full', className)}
      role='dialog'
      aria-label={t('agenda.monthHover.jobName')}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <div
        className={cn(
          'absolute z-[201] h-0 w-0 -translate-y-1/2 border-y-[7px] border-y-transparent',
          resolvedPointerSide === 'left'
            ? '-left-2 border-r-[8px] border-r-[#e7e9ef]'
            : '-right-2 border-l-[8px] border-l-[#e7e9ef]'
        )}
        style={{ top: position ? position.pointerTop : '50%' }}
        aria-hidden
      />
      <div className='overflow-hidden rounded-xl border border-[#e7e9ef] bg-white shadow-[0_4px_12px_rgba(26,27,30,0.25)]'>
        <div className='max-h-[320px] overflow-x-hidden overflow-y-auto [&>div]:overflow-x-hidden'>
          <Table className='w-full min-w-0 table-fixed'>
            <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
              <TableRow>
                <TableHead className='h-10 w-[48%] px-3 py-2 font-sans text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'>
                  {t('agenda.monthHover.jobName')}
                </TableHead>
                <TableHead className='h-10 w-[22%] px-3 py-2 font-sans text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'>
                  {t('agenda.monthHover.timeslot')}
                </TableHead>
                <TableHead className='h-10 w-[30%] px-3 py-2' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(job => {
                const address = formatPostalAddress(job)
                return (
                  <TableRow key={job.id} {...jobPreloadTargetProps(job)}>
                    <TableCell className='h-16 px-3 py-2 align-middle'>
                      <div className='flex min-w-0 flex-col gap-0.5'>
                        <span className='truncate font-sans text-sm font-medium leading-[1.4] text-[#171a20]'>
                          {job.reference ?? job.id.slice(0, 8)}
                        </span>
                        {address ? (
                          <span className='truncate font-sans text-xs font-normal leading-4 text-[#3d434e]'>
                            {address}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className='h-16 whitespace-nowrap px-3 py-2 align-middle font-sans text-sm font-medium leading-none text-[#171a20]'>
                      {formatTimeslot(job, t('agenda.monthHover.noTime'))}
                    </TableCell>
                    <TableCell className='h-16 px-2 py-2 align-middle'>
                      <Button
                        type='button'
                        variant='link'
                        className='h-8 w-full justify-end gap-1 px-1 py-2 font-sans text-sm font-medium leading-none text-[#ff5a1f]'
                        onClick={() => onViewJob(job)}
                      >
                        <span className='whitespace-nowrap'>
                          {t('agenda.monthHover.viewDetails')}
                        </span>
                        <ArrowRight className='h-4 w-4 shrink-0' />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )

  if (!anchorElement || typeof document === 'undefined') return content
  if (!position || position.anchorElement !== anchorElement) return null

  return createPortal(
    <div
      className='fixed z-[200]'
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
      }}
    >
      {content}
    </div>,
    document.body
  )
}
